import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import {
  Alert,
  Avatar,
  Box,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import NotesIcon from '@mui/icons-material/Notes'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import API from '../../../../services/API'
import { SiteModeContext } from '../../../../components/mainFrame/MainFrame'

const typeStyles = {
  Incident: { color: '#85276A', bg: '#FCE7F3' },
  System: { color: '#242F40', bg: '#E2E8F0' },
  Command: { color: '#E32525', bg: '#FEE2E2' },
  Decision: { color: '#85276A', bg: '#FCE7F3' },
  Log: { color: '#00964C', bg: '#DCFCE7' },
}

const Log = ({ log, isLast }) => {
  const { setSiteMode, setSelectedControlRoom } = useContext(SiteModeContext)
  const style = typeStyles[log.type] || { color: '#239AA4', bg: '#E6F7F9' }

  return (
    <Stack direction="row" spacing={2} position="relative">
      <Stack alignItems="center" sx={{ width: 42, flexShrink: 0 }}>
        <Avatar
          src={log?.author?.photograph}
          sx={{
            width: 38,
            height: 38,
            border: '3px solid #FFFFFF',
            boxShadow: '0 6px 18px rgba(36,47,64,0.18)',
          }}
        />
        {!isLast && (
          <Box
            sx={{
              width: 2,
              flex: 1,
              minHeight: 34,
              backgroundColor: '#E2E8F0',
              mt: 1,
            }}
          />
        )}
      </Stack>

      <Paper
        elevation={0}
        sx={{
          flex: 1,
          minWidth: 0,
          borderRadius: 4,
          p: 2,
          border: '1px solid #E2E8F0',
          boxShadow: '0 8px 24px rgba(36,47,64,0.06)',
        }}
      >
        <Stack spacing={1.25} sx={{ minWidth: 0 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            spacing={1}
          >
            <Stack>
              <Typography fontWeight={800} color="#242F40">
                {log.author.firstName} {log.author.lastName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {moment(log.createdAt).format('HH:mm [-] Do MMM YYYY')}
              </Typography>
            </Stack>

            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              sx={{ minWidth: 0, '& .MuiChip-root': { maxWidth: '100%' } }}
            >
              {log.type && (
                <Chip
                  label={log.type}
                  size="small"
                  sx={{
                    borderRadius: 999,
                    backgroundColor: style.bg,
                    color: style.color,
                    fontWeight: 800,
                  }}
                />
              )}

              {log?.controlRoom && (
                <Chip
                  label={
                    log?.controlRoom?.title ||
                    log?.controlRoom?.customTitle ||
                    log?.controlRoom?.linkedEvent?.title ||
                    'Control Room'
                  }
                  size="small"
                  sx={{
                    borderRadius: 999,
                    backgroundColor: '#E6F7F9',
                    color: '#1E7F88',
                    fontWeight: 800,
                  }}
                />
              )}
            </Stack>
          </Stack>

          <Typography variant="body2" color="#334155">
            {log.log ?? log.information}
          </Typography>

          {log.controlRoom && (
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              onClick={() => {
                setSiteMode('eventControl')
                setSelectedControlRoom(log.controlRoom._id)
              }}
              sx={{
                cursor: 'pointer',
                color: '#239AA4',
                fontWeight: 800,
                width: 'fit-content',
                '&:hover': { color: '#1E7F88' },
              }}
            >
              <Typography variant="body2" fontWeight={800}>
                View Control Room
              </Typography>
              <ArrowForwardIcon sx={{ fontSize: 18 }} />
            </Stack>
          )}
        </Stack>
      </Paper>
    </Stack>
  )
}

const RecentLogs = () => {
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [logs, setLogs] = useState([])

  useEffect(() => {
    API.get('/controlroominfo/dashboard/recentlogs')
      .then((res) => {
        if (res.status === 200) setLogs(res.data)
        else setFetchError(true)
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }, [])

  return (
    <Stack gap={2}>
      <Stack>
        <Typography variant="h6" fontWeight={900} color="#242F40">
          Recent Logs
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Latest operational updates across active control rooms.
        </Typography>
      </Stack>

      {loading && <LinearProgress />}
      {fetchError && (
        <Alert severity="error" sx={{ borderRadius: 3 }}>
          We're unable to fetch logs currently.
        </Alert>
      )}
      {!loading && !fetchError && logs.length === 0 && (
        <Alert severity="info" sx={{ borderRadius: 3 }}>
          There are no logs to display.
        </Alert>
      )}

      <Stack spacing={2}>
        {logs.map((x, index) => (
          <Log key={x._id} log={x} isLast={index === logs.length - 1} />
        ))}
      </Stack>
    </Stack>
  )
}

export default RecentLogs
